import { App, Avatar, Button, Drawer, Empty, Flex, Input, Select, Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import { readServerMessage } from "../helpers/server-message.ts"
import { getProjectDocumentReviewers } from "../../Project/api/project-documents.services.ts"
import {
  createDocumentComment,
  getDocumentComments,
  resolveDocumentComment
} from "../../Project/api/project-attachment-workflow.services.ts"
import { type DocumentComment } from "../../Project/types/project-attachment-workflow.types.ts"
import { type FileItem } from "../types/file-item.types.ts"

const { Text, Paragraph } = Typography

const authorName = (comment: DocumentComment) => getEntityName(comment.author)

interface CommentComposerProps {
  placeholder: string
  members: { label: string; value: string }[]
  onSubmit: (body: string, mentionedUserIds: string[]) => Promise<boolean>
  autoFocus?: boolean
  compact?: boolean
}

function CommentComposer({
  placeholder,
  members,
  onSubmit,
  autoFocus,
  compact
}: CommentComposerProps) {
  const { t } = useTranslation()
  const [body, setBody] = useState("")
  const [mentioned, setMentioned] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!body.trim()) return
    setSaving(true)
    const succeeded = await onSubmit(body.trim(), mentioned)
    setSaving(false)
    if (succeeded) {
      setBody("")
      setMentioned([])
    }
  }

  return (
    <Flex vertical gap={6}>
      <Input.TextArea
        autoFocus={autoFocus}
        disabled={saving}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={compact ? 2 : 3}
        maxLength={2000}
      />
      <Flex gap={6} align="center" wrap="wrap">
        <Select
          mode="multiple"
          size="small"
          allowClear
          value={mentioned}
          onChange={setMentioned}
          options={members}
          optionFilterProp="label"
          placeholder={t("files.comments.mentionPlaceholder")}
          className="min-w-40 flex-1"
        />
        <Button type="primary" size="small" loading={saving} onClick={() => void submit()}>
          {t("files.comments.submit")}
        </Button>
      </Flex>
    </Flex>
  )
}

function CommentThread({
  comment,
  members,
  onReply,
  onResolve
}: {
  comment: DocumentComment
  members: { label: string; value: string }[]
  onReply: (parentId: string, body: string, mentionedUserIds: string[]) => Promise<boolean>
  onResolve: (commentId: string) => Promise<void>
}) {
  const { t } = useTranslation()
  const [replying, setReplying] = useState(false)

  return (
    <Flex vertical gap={8}>
      <CommentRow comment={comment} />
      <Flex gap={12} className="pl-8">
        <Text
          type="secondary"
          className="text-xs cursor-pointer hover:!text-primary"
          onClick={() => setReplying((prev) => !prev)}
        >
          {t("files.comments.reply")}
        </Text>
        {!comment.resolved_at && (
          <Text
            type="secondary"
            className="text-xs cursor-pointer hover:!text-primary"
            onClick={() => void onResolve(comment.id)}
          >
            {t("files.comments.resolve")}
          </Text>
        )}
        {comment.resolved_at && (
          <Text type="secondary" className="text-xs">
            {t("files.comments.resolved")}
          </Text>
        )}
      </Flex>

      {comment.replies.map((reply) => (
        <div key={reply.id} className="pl-8">
          <CommentRow comment={reply} />
        </div>
      ))}

      {replying && (
        <div className="pl-8">
          <CommentComposer
            compact
            autoFocus
            placeholder={t("files.comments.replyPlaceholder")}
            members={members}
            onSubmit={async (body, mentionedUserIds) => {
              const succeeded = await onReply(comment.id, body, mentionedUserIds)
              if (succeeded) setReplying(false)
              return succeeded
            }}
          />
        </div>
      )}
    </Flex>
  )
}

function CommentRow({ comment }: { comment: DocumentComment }) {
  return (
    <Flex gap={8}>
      <Avatar size="small">{authorName(comment).charAt(0) || "?"}</Avatar>
      <Flex vertical gap={0} className="min-w-0 flex-1">
        <Flex gap={6} align="baseline">
          <Text strong className="text-sm">
            {authorName(comment)}
          </Text>
          <Text type="secondary" className="text-xs">
            {getFormatDate(comment.created) ?? comment.created}
          </Text>
        </Flex>
        <Paragraph className="!mb-0 text-sm break-words">{comment.body}</Paragraph>
      </Flex>
    </Flex>
  )
}

interface CommentsDrawerProps {
  file: FileItem
  onClose: () => void
}

export function CommentsDrawer({ file, onClose }: CommentsDrawerProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [comments, setComments] = useState<DocumentComment[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [members, setMembers] = useState<{ label: string; value: string }[]>([])
  const [reloadCount, setReloadCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    void getProjectDocumentReviewers(file.id).then((response) => {
      if (!cancelled && response.ok && response.data) setMembers(response.data)
    })
    return () => {
      cancelled = true
    }
  }, [file.id])

  useEffect(() => {
    let cancelled = false
    setComments(null)
    setLoadFailed(false)
    void getDocumentComments(file.id).then((response) => {
      if (cancelled) return
      if (response.ok && response.data) {
        setComments(response.data.results)
      } else {
        setComments([])
        setLoadFailed(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [file.id, reloadCount])

  const reload = () => setReloadCount((count) => count + 1)

  const postComment = async (
    body: string,
    mentionedUserIds: string[],
    parent?: string
  ): Promise<boolean> => {
    const response = await createDocumentComment({
      attachment: file.id,
      body,
      parent: parent ?? null,
      mentioned_user_ids: mentionedUserIds
    })
    if (!response.ok) {
      message.error(readServerMessage(response.data) ?? t("files.comments.postFailed"))
      return false
    }
    reload()
    return true
  }

  const resolve = async (commentId: string) => {
    const response = await resolveDocumentComment(commentId)
    if (!response.ok) {
      message.error(readServerMessage(response.data) ?? t("files.comments.resolveFailed"))
      return
    }
    reload()
  }

  return (
    // Default (portaled) container — see ReviewDrawer's note: this can open
    // from within the FileViewer modal, which an inline drawer would render behind.
    <Drawer
      title={t("files.comments.panelTitle", { name: file.name })}
      open
      onClose={onClose}
      width={400}
      maskClosable
    >
      <Flex vertical gap="large" className="h-full">
        <Flex vertical gap="middle" className="flex-1 overflow-y-auto">
          {comments === null ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : loadFailed ? (
            <Empty description={t("files.comments.loadFailed")}>
              <Button size="small" onClick={reload}>
                {t("files.comments.retry")}
              </Button>
            </Empty>
          ) : comments.length === 0 ? (
            <Empty description={t("files.comments.empty")} />
          ) : (
            comments.map((comment) => (
              <CommentThread
                key={comment.id}
                comment={comment}
                members={members}
                onReply={(parentId, body, mentionedUserIds) =>
                  postComment(body, mentionedUserIds, parentId)
                }
                onResolve={resolve}
              />
            ))
          )}
        </Flex>

        <CommentComposer
          placeholder={t("files.comments.writePlaceholder")}
          members={members}
          onSubmit={(body, mentionedUserIds) => postComment(body, mentionedUserIds)}
        />
      </Flex>
    </Drawer>
  )
}
